<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="URLフィルタリングプロファイル設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer
        v-slot="{ handleSubmit }"
        :initial-values="{
          blackList: form.blackList,
          whiteList: form.whiteList,
        }"
        as="div"
      >
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- 名前 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="名前"
                      :msg="
                        $filter.message('info_alpha_num', { maxLength: '31' })
                      "
                      placement="end"
                    />
                    <validation-provider
                      name="profileUrlName"
                      v-model="form.profileUrlName"
                      label="名前"
                      :rules="{
                        required: true,
                        max: { length: 31 },
                        is_half_width_alpha_numeric: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- ブラックリスト -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ブラックリスト"
                      :msg="
                        'http(s)://以降を半角英数記号255文字以内で入力してください。' +
                        '\n' +
                        '注意事項についてマニュアルをご確認ください。'
                      "
                      :isRequired="form.blackList.length > 0"
                      placement="end"
                    />
                    <validation-array
                      name="blackList"
                      v-slot="{ fields, push, remove }"
                    >
                      <template v-for="(item, index) in fields" :key="item.key">
                        <v-row class="ga-2 flex-nowrap">
                          <validation-provider
                            :name="`blackList_${item.key}`"
                            v-model="form.blackList[index]"
                            label="ブラックリスト"
                            :rules="{
                              required: form.blackList.length !== 0,
                              max: { length: 255 },
                              is_full_width_alpha_numeric: true,
                              is_url_without_restricted_phrase: true,
                              is_url_without_wildcard: true,
                              duplicate: { list: blacklistList(index) },
                            }"
                            v-slot="{ componentField, errorMessage, meta }"
                            :keepValue="true"
                          >
                            <app-form-input
                              v-bind="componentField"
                              :field-meta="meta"
                              :error-messages="errorMessage"
                            />
                          </validation-provider>

                          <v-btn
                            icon="$mdiMinus"
                            size="small"
                            variant="outlined"
                            class="rounded-sm"
                            @click="removeBlackListURL(index, remove)"
                          />
                        </v-row>
                      </template>
                      <v-row justify="end">
                        <v-btn
                          icon="$mdiPlus"
                          size="small"
                          variant="outlined"
                          class="rounded-sm"
                          @click="addBlackListURL(push)"
                        />
                      </v-row>
                    </validation-array>
                  </v-col>
                </v-row>
                <!-- ブラックリストの動作 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ブラックリストの動作"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-radio-group
                      inline
                      v-model="form.blackListAction"
                      hide-details
                    >
                      <v-radio label="Alert" value="ALERT" />
                      <v-radio label="Block" value="BLOCK" />
                      <v-radio label="Continue" value="CONTINUE" />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <!-- ホワイトリスト -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="ホワイトリスト"
                      :msg="
                        'http(s)://以降を半角英数記号255文字以内で入力してください。' +
                        '\n' +
                        '注意事項についてマニュアルをご確認ください。' +
                        '\n' +
                        'ホワイトリストの動作はallow(許可しログは取得しない)となります。'
                      "
                      :isRequired="form.whiteList.length > 0"
                      placement="end"
                    />
                    <validation-array
                      name="whiteList"
                      v-slot="{ fields, push, remove }"
                    >
                      <template v-for="(item, index) in fields" :key="item.key">
                        <v-row class="ga-2 flex-nowrap">
                          <validation-provider
                            :name="`whiteList_${item.key}`"
                            v-model="form.whiteList[index]"
                            label="ホワイトリスト"
                            :rules="{
                              required: form.whiteList.length !== 0,
                              max: { length: 255 },
                              is_full_width_alpha_numeric: true,
                              is_url_without_restricted_phrase: true,
                              is_url_without_wildcard: true,
                              duplicate: { list: whitelistList(index) },
                            }"
                            v-slot="{ componentField, errorMessage, meta }"
                            :keepValue="true"
                          >
                            <app-form-input
                              v-bind="componentField"
                              :field-meta="meta"
                              :error-messages="errorMessage"
                            />
                          </validation-provider>

                          <v-btn
                            icon="$mdiMinus"
                            size="small"
                            variant="outlined"
                            class="rounded-sm"
                            @click="removeWhiteListURL(index, remove)"
                          />
                        </v-row>
                      </template>
                      <v-row justify="end">
                        <v-btn
                          icon="$mdiPlus"
                          size="small"
                          variant="outlined"
                          class="rounded-sm"
                          @click="addWhiteListURL(push)"
                        />
                      </v-row>
                    </validation-array>
                  </v-col>
                </v-row>
                <!-- カテゴリ -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="カテゴリ"
                      :isRequired="false"
                      :isInfo="false"
                    />
                    <v-btn-toggle
                      density="compact"
                      variant="outlined"
                      divided
                      color="primary"
                    >
                      <v-btn @click="allChangeCategoryAction('ALLOW')"
                        >一括設定 Allow</v-btn
                      >
                      <v-btn @click="allChangeCategoryAction('ALERT')"
                        >一括設定 Alert</v-btn
                      >
                      <v-btn @click="allChangeCategoryAction('BLOCK')"
                        >一括設定 Block</v-btn
                      >
                      <v-btn @click="allChangeCategoryAction('CONTINUE')"
                        >一括設定 Continue</v-btn
                      >
                    </v-btn-toggle>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <div>
                      <v-table>
                        <thead class="v-table-head">
                          <tr>
                            <th class="text-center">カテゴリ名</th>
                            <th class="text-center th-action">アクション</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template
                            v-for="(category, idx) in form.categoryList"
                            :key="idx"
                          >
                            <tr>
                              <td>{{ category.categoryName }}</td>
                              <td>
                                <v-btn-toggle
                                  density="compact"
                                  variant="outlined"
                                  divided
                                  mandatory
                                  v-model="category.actionType"
                                >
                                  <v-btn value="ALLOW">Allow</v-btn>
                                  <v-btn value="ALERT">Alert</v-btn>
                                  <v-btn value="BLOCK">Block</v-btn>
                                  <v-btn value="CONTINUE">Continue</v-btn>
                                </v-btn-toggle>
                              </td>
                            </tr>
                          </template>
                        </tbody>
                      </v-table>
                    </div>
                  </v-col>
                </v-row>
                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :isRequired="false"
                      placement="end"
                    />
                    <validation-provider
                      name="description"
                      v-model="form.description"
                      label="備考"
                      :rules="{
                        max: { length: 20 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  urlProfile ? "変更" : "追加"
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { Category, SetUrlProfile, UrlProfile } from "@/apis/UrlProfileApi";
import cloneDeep from "lodash/cloneDeep";

export default defineComponent({
  name: "ProfileUrlFilteringEdit",
  props: {
    urlProfile: {
      type: Object as PropType<UrlProfile | null>,
      required: false,
      default: null,
    },
    profileUrlSeq: {
      type: String as PropType<string>,
      required: false,
      default: "",
    },
    type4Id: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
    profileUrlKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      isLoaded: false,
      form: {
        blackList: [] as string[],
        blackListAction: "ALERT",
        whiteList: [] as string[],
      } as UrlProfile,
    };
  },
  computed: {
    /** ブラックリストの重複チェック用リスト作成 */
    blacklistList() {
      return (index: number) => {
        return this.form.blackList.filter((_, num) => num !== index);
      };
    },
    /** ホワイトリストの重複チェック用リスト作成 */
    whitelistList() {
      return (index: number) => {
        return this.form.whiteList.filter((_, num) => num !== index);
      };
    },
  },
  async mounted() {
    if (this.urlProfile) {
      // 変更時 階層があるオブジェクトのためlodashのcloneDeepで対応
      this.form = cloneDeep(this.urlProfile);
      if (this.form.blackList.length === 0) {
        this.form.blackList.push("");
      }
      if (this.form.whiteList.length === 0) {
        this.form.whiteList.push("");
      }
    } else {
      // 追加時 カテゴリは既存のデータを初期設定する
      await this.load();
    }
    this.isLoaded = true;
  },
  methods: {
    async load() {
      // vue3ではリアクティブ性を損なわずにオブジェクトに新しいプロパティを追加することが可能
      this.form["categoryList"] = (
        await this.$api.urlProfileApi.getCategories()
      ).categoryList;
    },

    async submit() {
      // 確認ダイアログの表示
      const action = this.urlProfile ? "変更" : "追加";
      await this.$confirm(`プロファイルを${action}します。よろしいですか？`);

      // データの設定
      // ブラックリスト1件かつ未入力は空配列を設定
      // ホワイトリスト1件かつ未入力は空配列を設定
      const reqData: SetUrlProfile = {
        profileUrlName: this.form.profileUrlName,
        blackList: this.isEmptyList(this.form.blackList)
          ? []
          : this.form.blackList,
        blackListAction: this.form.blackListAction,
        whiteList: this.isEmptyList(this.form.whiteList)
          ? []
          : this.form.whiteList,
        categoryList: this.form.categoryList,
        description: this.form.description || null,
        profileUrlKeyId: this.profileUrlKeyId,
        note: null,
      };

      // // プロファイル(URLフィルタリング)の登録・更新API呼び出し
      if (this.urlProfile) {
        await this.modUrlProfile(reqData);
      } else {
        await this.addUrlProfile(reqData);
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /**
     * ブラックリスト・ホワイトリストの入力フォームが1個かつ、未入力の場合trueを返す
     * @param list チェック対象のリスト
     */
    isEmptyList(list: string[]): boolean {
      return list.length === 1 && !list[0];
    },

    /** プロファイル(URLフィルタリング)の追加処理 */
    async addUrlProfile(reqData: SetUrlProfile) {
      // Type4IDの有無により呼び出すAPIをハンドリング
      if (!this.type4Id) {
        await this.$api.urlProfileApi.postType1UrlFiltering(reqData);
      } else {
        await this.$api.urlProfileApi.postType4UrlFiltering(
          this.type4Id,
          reqData
        );
      }
    },

    /** プロファイル(URLフィルタリング)の更新処理 */
    async modUrlProfile(reqData: SetUrlProfile) {
      // Type4IDの有無により呼び出すAPIをハンドリング
      if (!this.type4Id) {
        await this.$api.urlProfileApi.putType1UrlFiltering(
          this.profileUrlSeq,
          reqData
        );
      } else {
        await this.$api.urlProfileApi.putType4UrlFiltering(
          this.type4Id,
          this.profileUrlSeq,
          reqData
        );
      }
    },

    /** ブラックリストURL追加 */
    addBlackListURL(push: (item: any) => void) {
      const addItem = "";
      this.form.blackList.push(addItem);
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },

    /** ブラックリストURL削除 */
    removeBlackListURL(index: number, remove: (idx: number) => void) {
      // 入力フォームが全て無くなる状態はポリシーに反するので、最後の1つは空文字にする
      if (this.form.blackList.length === 0) {
        this.form.blackList = [""];
      }
      this.form.blackList.splice(index, 1);
      // ValidationにFieldを削除することを伝える
      remove(index);
    },

    /** ホワイトリストURL追加 */
    addWhiteListURL(push: (item: any) => void) {
      const addItem = "";
      this.form.whiteList.push(addItem);
      // ValidationにFieldを追加することを伝える
      push(addItem);
    },

    /** ホワイトリストURL削除 */
    removeWhiteListURL(index: number, remove: (idx: number) => void) {
      // 入力フォームが全て無くなる状態はポリシーに反するので、最後の1つは空文字にする
      if (this.form.whiteList.length === 0) {
        this.form.whiteList = [""];
      }
      this.form.whiteList.splice(index, 1);
      // ValidationにFieldを削除することを伝える
      remove(index);
    },

    /** カテゴリのアクションを一括変更 */
    allChangeCategoryAction(action: Category["actionType"]) {
      // 全てのアクションを押下したボタンのアクションに変更する
      this.form.categoryList.forEach((v) => (v.actionType = action));
    },
  },
});
</script>

<style lang="scss" scoped>
.v-table {
  th {
    height: 45px !important;
  }
}
.th-action {
  width: 270px;
}
</style>
